import { pluralize } from "@suse-o11y/pluralize";
import { useComputed } from "@suse-o11y/signal";
import { Template } from "@suse-o11y/template";
import React from "react";
import { useSelector } from "react-redux";

import { selectTimestampDate } from "../../../../../app/state/view/timeline/TimelineSelectors";
import { DashboardChartPluginSpec, DashboardWidgetSpec } from "../../../../../common/dashboard-spec";
import { TimeSeriesQuery } from "../../../../../common/dashboard-spec/_time-series-query";
import { MetricsDrawer } from "../../../../../components/metrics-drawer";
import { useTimelineInterval } from "../../../../../components/react/timeline/useTimelineInterval";
import { fromMetricQuery as widgetQueryFromMetricQuery } from "../../../../../components/widget-form/query/spec";
import { ChartWidget } from "../../../../../components/widget-form/widget";
import { generatePath } from "../../../../../generic/routing";
import { COMPONENT_PATH } from "../../../../../routes/Routes";
import { MonitorStatusDrawer } from "../../../highlights-perspective/monitor-status-drawer/MonitorStatusDrawer";
import { EastPaneLayout } from "../EastPaneLayout";
import { EastPaneNotFound } from "../EastPaneNotFound";

import { MonitorStatus } from "./_monitor-status";
import { usePinnedItemsState } from "./_pinned-items-context";
import { PinnedItemsSection } from "./_pinned-items-section";
import type { PinnedMetric } from "./metric";

const pluralizeMonitorStatus = pluralize({
	1: "Monitor status",
	more: "Monitor statuses",
});

const pluralizeMetric = pluralize({
	1: "Metric",
	more: "Metrics",
});

function pinnedMetricToDashboardWidgetSpec(item: PinnedMetric): DashboardWidgetSpec {
	return DashboardWidgetSpec.of({
		name: Template(item.name ?? ""),
		plugin: {
			queries: item.metricQueries.map((query) => TimeSeriesQuery.fromQuerySpec(widgetQueryFromMetricQuery(query))),
			links: [
				{
					url: Template(`/#${generatePath(COMPONENT_PATH, { identifier: item.component.identifier })}`),
					name: Template(`${item.component.name} (${item.component.type})`),
					targetBlank: false,
				},
			],
			...DashboardChartPluginSpec.fromChartSpec(item.chart),
		},
	});
}

export const PinnedItems: React.FC = () => {
	const timestamp = useSelector(selectTimestampDate);
	const timeRange = useTimelineInterval();
	const timeRangeValue = useComputed(timeRange);
	const state = usePinnedItemsState();
	const isPinnedItemsEmpty = useComputed((monitor) => state.size(monitor) === 0, [state]);

	if (isPinnedItemsEmpty) {
		return <EastPaneNotFound type="PinnedItems" />;
	}

	return (
		<EastPaneLayout>
			<div>
				<PinnedItemsSection
					enlargeDialog={(monitorStatus) => (
						<MonitorStatusDrawer
							monitorStatusIdentifier={monitorStatus.identifier}
							timeRange={timeRangeValue}
							timestamp={monitorStatus.topologyTime}
						/>
					)}
					getCardComponentReference={({ component, topologyTime }) => ({
						...component,
						timestamp: topologyTime,
					})}
					getCardDescription={({ monitorDescription }) => monitorDescription}
					getCardTitle={({ message }) => message}
					name={pluralizeMonitorStatus}
					state={state.monitorStatuses}
				>
					{({ timeRange: pinnedTimeRange, ...monitorStatus }) => (
						<MonitorStatus monitorStatus={monitorStatus} timeRange={pinnedTimeRange} />
					)}
				</PinnedItemsSection>

				<PinnedItemsSection
					enlargeDialog={(metric) => (
						<MetricsDrawer
							chart={metric.chart}
							componentIdentifier={metric.component.identifier}
							metricQueries={metric.metricQueries}
							timeRange={timeRangeValue}
							timestamp={timestamp}
							title={metric.name}
						/>
					)}
					getCardComponentReference={({ component }) => component}
					getCardDescription={({ description }) => description}
					getCardTitle={({ name }) => name}
					name={pluralizeMetric}
					state={state.metrics}
					toDashboardWidgetSpec={pinnedMetricToDashboardWidgetSpec}
				>
					{(metric) => (
						<ChartWidget
							batch={metric.name ?? false}
							chart={metric.chart}
							onTimeRangeChange={(nextTimeRange) => timeRange.write(nextTimeRange)}
							queries={metric.metricQueries}
							timeRange={timeRangeValue}
							timestamp={timestamp}
						/>
					)}
				</PinnedItemsSection>
			</div>
		</EastPaneLayout>
	);
};
